import { Overlay, OverlayContainer, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  QueryList,
  Renderer2,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl } from '@angular/forms';
import { merge, Subscription } from 'rxjs';
import { FADE_IN_OUT_ANIMATION } from './../../animations/animations';
import { ERRORS_KEYS } from './../error/error.component';
import { OptionComponent } from './option/option.component';

@Component({
    selector: 'gl-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    animations: [FADE_IN_OUT_ANIMATION],
    standalone: false
})
export class SelectComponent
  implements ControlValueAccessor, AfterContentInit, OnDestroy, OnChanges
{
  @Input() public errorAboveInput: boolean;
  @Input() public isLong = false;
  @Input() public isLonger = false;
  @ViewChild('list', { static: false }) public listContent: TemplateRef<any>;
  @Input() public placeholder: string;
  @ContentChildren(OptionComponent) public options: QueryList<OptionComponent>;
  @Input() @HostBinding('class.disabled') public isDisabled;
  @Input() public disableSelect = false;
  @Input() public searchOption: boolean;
  @Output() public searchEmit = new EventEmitter();
  public chosenOption: { component: OptionComponent; text: string };
  public open = false;
  public errorsKeys = ERRORS_KEYS;
  public value: any;
  @Input() public defaultValue: any;
  public overlayRef: OverlayRef;
  private isAfterViewInit: boolean;
  private portal: TemplatePortal<any>;
  private subscriptions: Subscription[] = [];
  private optionsSubscription: Subscription[] = [];
  private isMouseOverList = false;
  private eventListener: EventListener;
  @Output() public selectionChange = new EventEmitter<any>();
  constructor(
    private overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private overlayContainer: OverlayContainer,
    private renderer: Renderer2,
    private elRef: ElementRef<HTMLInputElement>,
    @Optional() public parentFormGroup: FormGroupDirective,
    @Optional()
    @Self()
    public controlDir: NgControl,
  ) {
    if (controlDir) {
      controlDir.valueAccessor = this;
    }
  }

  @HostBinding('class.error') get submitted() {
    return this.parentFormGroup?.submitted && this.controlDir.invalid;
  }

  public ngOnDestroy(): void {
    const containerElement = document.querySelector('.container');
    if (containerElement) {
      containerElement.removeEventListener('scroll', this.onScroll.bind(this));
    }
    window.removeEventListener('wheel', this.onScroll.bind(this));
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.hideList();
  }

  @HostListener('click')
  public onclick() {
    if (!this.open && !this.isDisabled && !this.disableSelect) {
      this.showList();
    } else {
      this.hideList();
    }
  }

  public ngAfterContentInit(): void {
    if (this.defaultValue) {
      this.value = this.defaultValue;
    }

    setTimeout(() => {
      this.setUsedOption();
    });
    this.isAfterViewInit = true;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isDisabled && changes.isDisabled.currentValue) {
      this.setDisabledState(changes.isDisabled.currentValue);
    }
  }

  public onSearch(value: string) {
    this.searchEmit.emit(value);
  }

  public onClickedOutside() {
    if (this.open) {
      this.open = false;
      this.hideList();
    }
  }

  public registerOnChange(fn: (_: any) => void): void {
    this.propagateChange = fn;
  }

  public registerOnTouched = (_: any) => undefined;

  public writeValue(value: any): void {
    if (value) {
      this.value = value;
      if (this.isAfterViewInit) {
        this.setUsedOption();
      }
    } else {
      this.chosenOption = null;
    }
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.renderer.setProperty(this.elRef.nativeElement, 'disabled', isDisabled);
    this.hideList();
  }

  public getErrorObjectFirstTranslationKey() {
    return this.errorsKeys[Object.keys(this.controlDir.errors)[0]] as string;
  }

  private propagateChange = (_: any) => undefined;

  private setUsedOption() {
    const component = this.options.find((option) => option.value === this.value);
    if (component) {
      const chosenOption = component.getOption();
      this.chosenOption = chosenOption;
    }
  }

  public onMouseEnter(): void {
    this.isMouseOverList = true;
  }

  public onMouseLeave(): void {
    this.isMouseOverList = false;
  }

  public onScroll(): void {
    if (this.searchOption) {
      return;
    }
    if (this.isMouseOverList) {
      return;
    }
    this.hideList();
  }

  private showList() {
    this.open = true;
    this.onSearch('');
    this.overlayRef = this.overlay.create({
      positionStrategy: this.generatePositionStrategy(),
      width: this.elRef.nativeElement.clientWidth,
    });
    this.portal = new TemplatePortal(this.listContent, this._viewContainerRef);
    this.overlayRef.attach(this.portal);
    this.createOptionsSubscription();
    this.eventListener = this.onScroll.bind(this);
    window.addEventListener('wheel', this.eventListener);
    const listElement = this.overlayRef.overlayElement.querySelector('.select-list');
    if (listElement) {
      listElement.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      listElement.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }

    this.subscriptions.push(
      this.options.changes.subscribe(() => {
        this.optionsSubscription.forEach((s) => s.unsubscribe());
        this.createOptionsSubscription();
      }),
    );
  }

  private createOptionsSubscription() {
    this.optionsSubscription.push(
      merge(...this.options.map((option) => option.optionClicked)).subscribe(
        (data: { component: OptionComponent; text: string }) => {
          this.chosenOption = data;
          this.propagateChange(this.chosenOption.component.value);
          this.selectionChange.emit(this.chosenOption.component.value);
          this.hideList();
        },
      ),
    );
  }

  private hideList() {
    this.open = false;
    if (this.overlayRef) {
      const listElement = this.overlayRef.overlayElement.querySelector('.select-list');
      if (listElement) {
        listElement.removeEventListener('mouseenter', this.onMouseEnter.bind(this));
        listElement.removeEventListener('mouseleave', this.onMouseLeave.bind(this));
      }
      this.overlayRef.detach();
      this.renderer.removeStyle(this.overlayContainer.getContainerElement(), 'z-index');
    }
    window.removeEventListener('wheel', this.eventListener);
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.optionsSubscription.forEach((s) => s.unsubscribe());
  }

  private generatePositionStrategy(): PositionStrategy {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elRef)
      .withFlexibleDimensions(true)
      .withPush(true)
      .withViewportMargin(20)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]);

    return positionStrategy;
  }
}
